/* Offline sync outbox (PWA polish P2). Pure localStorage queue of writes that
   failed while offline; sync.js flushes them on reconnect.

   Queues are namespaced per uid (`byb.outbox.<uid>`) so a shared device never
   replays one account's ops under another account's JWT: the RPC ops carry no
   uid — the server stamps auth.uid() — so an unscoped queue would credit
   user A's queued drills to whoever signs in next. Sign-out needs no cleanup;
   a user's queue simply parks until they sign back in. enqueue drops ops when
   nobody is signed in (callers already bail unauthenticated anyway).

   The only app import is `getUidSync` from supabase.js, which imports nothing
   from app libs — so the network libs can still import `enqueue` without a
   circular dependency.

   Op shape: { id, kind, key?, payload, tries }
     - kind:  "shoe" | "drill" | "speed" | "cardval" | "curriculum"
     - key:   optional coalesce key. Ops with the same kind+key replace each
              other (e.g. a shoe id, whose latest state is the only one worth
              replaying — the upsert is idempotent). Keyless ops (additive
              drill/speed/cardval deltas) always append.
     - tries: failed flush attempts; sync.js dead-letters the op after a cap
              so one permanently-rejected op can't wedge the queue forever. */
import { getUidSync } from "./supabase";

const LEGACY_KEY = "byb.outbox"; // pre-namespacing key, migrated on flush
const keyFor = (uid) => `byb.outbox.${uid}`;
const deadKeyFor = (uid) => `byb.outbox.dead.${uid}`;
const DEAD_MAX = 20; // debug stash cap — oldest retired ops fall off
let seq = 0;

function read(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function write(key, ops) {
  try {
    localStorage.setItem(key, JSON.stringify(ops));
  } catch {
    /* quota / disabled — best-effort, drop silently */
  }
}

export function getQueue(uid = getUidSync()) {
  return uid ? read(keyFor(uid)) : [];
}

/* Queue writes degrade in stages on quota exhaustion instead of silently
   dropping everything (CODE-REVIEW logic #8):
     1. plain write;
     2. drop the dead-letter stash (debug-only) and regenerable byb.cache.*
        entries, retry;
     3. strip `state` from queued shoe ops — the resume snapshot is the
        heavyweight, and history sync works without it — retry;
     4. drop oldest ops until the newest fit.
   Only stage 4 loses queued writes, and it keeps the most recent ones. */
function setQueue(uid, ops) {
  if (!uid) return;
  const key = keyFor(uid);
  const attempt = (arr) => {
    localStorage.setItem(key, JSON.stringify(arr));
    return true;
  };
  try {
    return void attempt(ops);
  } catch {
    /* fall through to staged eviction */
  }
  try {
    localStorage.removeItem(deadKeyFor(uid));
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const kk = localStorage.key(i);
      if (kk && kk.startsWith("byb.cache.")) localStorage.removeItem(kk);
    }
    return void attempt(ops);
  } catch {
    /* fall through */
  }
  const slim = ops.map((o) =>
    o.kind === "shoe" && o.payload?.state
      ? { ...o, payload: { ...o.payload, state: null } }
      : o,
  );
  try {
    return void attempt(slim);
  } catch {
    /* fall through */
  }
  for (let keep = slim.length - 1; keep >= 1; keep--) {
    try {
      attempt(slim.slice(-keep));
      console.warn(`outbox: quota exhausted — kept newest ${keep} of ${ops.length} ops`);
      return;
    } catch {
      /* keep shrinking */
    }
  }
  console.warn("outbox: quota exhausted — could not persist any ops");
}

export function enqueue({ kind, key = null, payload }) {
  const uid = getUidSync();
  if (!uid) return;
  const ops = getQueue(uid);
  const next =
    key != null ? ops.filter((o) => !(o.kind === kind && o.key === key)) : ops;
  next.push({ id: `${Date.now()}-${seq++}`, kind, key, payload, tries: 0 });
  setQueue(uid, next);
  // Nudge sync.js (which listens in initSync) to flush shortly, so an op
  // queued mid-session retries without waiting for an online/visibility
  // event. Decoupled via an event to keep this file import-free of sync.js.
  try {
    window.dispatchEvent(new Event("byb:outbox-enqueued"));
  } catch {
    /* non-browser context */
  }
}

export function removeOp(id, uid = getUidSync()) {
  setQueue(uid, getQueue(uid).filter((o) => o.id !== id));
}

/* Drop queued ops for a kind+key after a direct write for the same row
   succeeded: the queued payload is by definition older, and replaying it
   later would regress the row. */
export function removeOps(kind, key, uid = getUidSync()) {
  setQueue(uid, getQueue(uid).filter((o) => !(o.kind === kind && o.key === key)));
}

/* Record a failed flush attempt on an op; returns the new count. */
export function bumpTries(id, uid = getUidSync()) {
  let tries = 0;
  const ops = getQueue(uid).map((o) =>
    o.id === id ? { ...o, tries: (tries = (o.tries ?? 0) + 1) } : o,
  );
  setQueue(uid, ops);
  return tries;
}

/* Retire a poison op: out of the queue, into a capped stash so the data is
   inspectable (localStorage `byb.outbox.dead.<uid>`) rather than destroyed. */
export function deadLetter(id, uid = getUidSync()) {
  if (!uid) return;
  const op = getQueue(uid).find((o) => o.id === id);
  removeOp(id, uid);
  if (!op) return;
  const dead = [...read(deadKeyFor(uid)), { ...op, deadAt: new Date().toISOString() }];
  write(deadKeyFor(uid), dead.slice(-DEAD_MAX));
  console.warn(`outbox: dead-lettered ${op.kind} op after ${op.tries} failed flushes`);
}

/* One-time move of the pre-namespacing queue into the signed-in user's queue,
   prepended (legacy ops predate anything enqueued since). Shoe ops stamped
   with a *different* user_id are dropped: they belong to another account and
   would only RLS-fail forever. Keyless RPC deltas carry no uid and migrate
   as-is — same attribution the old unscoped flush would have used. */
export function migrateLegacyQueue(uid) {
  if (!uid) return;
  const legacy = read(LEGACY_KEY);
  if (legacy.length) {
    const keep = legacy
      .filter((o) => o.kind !== "shoe" || o.payload?.user_id === uid)
      .map((o) => ({ tries: 0, ...o }));
    if (keep.length) setQueue(uid, [...keep, ...getQueue(uid)]);
  }
  try {
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    /* ignore */
  }
}

export function pendingCount() {
  return getQueue().length;
}
